import JSZip from "jszip";
import _axios from "axios";
import { AxiosResponse } from "axios";

type BuildPromptInput = {
  jobDescription: string;
  resumeContent: string;
  jobDescriptionFileUrl?: string;
  resumeFileUrl?: string;
  editableFields?: Array<{
    sectionId: string;
    sectionName: string;
    itemLabel: string;
    fieldPath: string;
    value: string;
  }>;
};

export type ResumeOptimizationInput = BuildPromptInput;

type PromptResponse = {
  message?: string | number;
};

const resumeOptimizationAxios = _axios.create({ baseURL: "/api", withCredentials: true });
const resumeExtractionAxios = _axios.create({ baseURL: "/api", withCredentials: true });

export const buildResumeOptimizationPrompt = ({
  jobDescription,
  resumeContent,
  jobDescriptionFileUrl,
  resumeFileUrl,
  editableFields,
}: BuildPromptInput) => {
  const fileReferenceBlock =
    jobDescriptionFileUrl || resumeFileUrl
      ? `
Uploaded File References:
Read uploaded file content from the URLs when file URLs are provided.
${jobDescriptionFileUrl ? `Job Description File URL: ${jobDescriptionFileUrl}` : ""}
${resumeFileUrl ? `Resume File URL: ${resumeFileUrl}` : ""}
`
      : "";

  return `You are an expert resume optimization assistant.
Optimize the candidate resume for the target job description and return reviewable section suggestions.
Rules:
- Return JSON only.
- Preserve truthful candidate information.
- Do not invent employers, degrees, dates, metrics, or tools.
- Improve wording, keyword alignment, bullet strength, and role relevance.
- Keep the content ATS-friendly and concise.
- If a section is weak, rewrite it with clearer impact while staying faithful to the source.
- Suggest changes only for the editable fields listed below.
- Keep each fieldPath exactly as provided.
- previousText must match the original editable field value.
- suggestedText must contain the improved replacement text for that same field.
- If no useful change is needed, omit that field.
JSON shape:
{
  "suggestions": [
    {
      "sectionId": "string",
      "sectionName": "string",
      "itemLabel": "string",
      "fieldPath": "string",
      "previousText": "string",
      "suggestedText": "string"
    }
  ]
}
Target Job Description:
"""
${jobDescription.trim()}
"""
Candidate Resume:
"""
${resumeContent.trim()}
"""
${fileReferenceBlock}
Editable Fields:
${JSON.stringify(editableFields ?? [], null, 2)}
Return JSON only:`;
};

export const optimizeResumeForJob = async (input: ResumeOptimizationInput) => {
  const response = await resumeOptimizationAxios.post<
    PromptResponse,
    AxiosResponse<PromptResponse>
  >("/auth/resumeOptimizationPrompt", { prompt: buildResumeOptimizationPrompt(input) });

  const result = response.data;

  if (result.message === 0) {
    throw new Error("OpenAI did not return an optimized resume.");
  }

  return typeof result.message === "string" ? result.message.trim() : "";
};

export const fetchUploadedFileText = async (url: string, label = "Resume") => {
  const response = await resumeExtractionAxios.post<{ text?: string }>(
    "/auth/extractUploadedFileContent",
    { url, label },
  );

  return response.data.text?.trim() ?? "";
};

const stripXml = (xml: string) =>
  xml
    .replace(/<w:tab\/>/g, " ")
    .replace(/<\/w:p>/g, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/[ \t]+/g, " ")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

const extractDocxText = async (file: File) => {
  const zip = await JSZip.loadAsync(await file.arrayBuffer());
  const documentXml = await zip.file("word/document.xml")?.async("string");
  if (!documentXml) throw new Error("Could not read text from this DOCX file.");
  return stripXml(documentXml);
};

const extractPdfTextBestEffort = async (file: File) => {
  const raw = new TextDecoder("latin1").decode(await file.arrayBuffer());
  const matches = Array.from(raw.matchAll(/\(([^()]{2,})\)\s*Tj/g), (match) => match[1]);
  const text = matches.length > 0 ? matches.join(" ") : raw.replace(/[^\x20-\x7E\n]+/g, " ");
  return text.replace(/\s+/g, " ").trim();
};

export const extractTextFromUpload = async (file: File) => {
  const extension = file.name.split(".").pop()?.toLowerCase();
  if (extension === "docx") return extractDocxText(file);
  if (extension === "pdf") return extractPdfTextBestEffort(file);
  if (extension === "txt") return file.text();
  throw new Error("Please upload a PDF, DOCX, or TXT file.");
};
